import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import QRCode from "qrcode";
import { toast } from "sonner";
import {
  Wallet, QrCode, Copy, Utensils, Bus, BookOpen, HeartPulse, Trophy,
  ArrowDownLeft, ArrowUpRight, Plus, Send, Sparkles, Clock, ShieldCheck,
  CheckCircle2, XCircle, Hourglass, MapPin, Sun, Moon, Bell,
  GraduationCap, Wifi, WifiOff, Home, MessageSquare, ChevronRight,
  Sunrise, Sunset, Coffee, Users, BedDouble,
  BookMarked, ClipboardCheck, TrendingUp, Zap, Flag, Award, Target,
  FileText, PlayCircle, Library, FlaskConical, Music, PartyPopper, Star,
  Flame, Rocket, Palette, Smile, Mic, Paperclip, ArrowUp, Pin, Radio,
  Compass, Feather, Cloud, CloudRain, Snowflake, Droplets, Leaf, Heart,
  Lightbulb, Timer, CircleDot, Trash2, Pencil, PlusCircle,
} from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { fmtUGX, shortDateTime } from "@/lib/format";
import { NotificationsBell } from "@/components/notifications-bell";
import { KycOnboarding } from "@/components/kyc-onboarding";
import { LiveBusMap } from "@/components/live-bus-map";
import {
  QuickActionsRich, BusCheckInDialog, WellbeingCard, ClubsBoard,
  MealsServiceCardV2, LibraryLabCardV2, ServicesHub, FundredPayCardShowcase,
  StudyLibrary,
} from "./student-extras";

// ---------- Types ----------
type Tx = { id: string; type: string; amount_ugx: number; description: string | null; created_at: string; status: string };
type BusCheckIn = { id: string; route: string; direction: string; stop: string | null; location: string | null; boarded_at: string };
type HealthEntry = { id: string; visit_date: string; reason: string; nurse_note: string | null; status: string; follow_up: string | null };
type AllowanceReq = { id: string; amount_ugx: number; reason: string; status: string; created_at: string; decided_at: string | null };
type Slot = { id: string; day_of_week: number; start_time: string; end_time: string; room: string | null; teacher_name: string | null; subject_id: string | null; class_label: string };
type Subject = { id: string; name: string; code: string; color_hex: string | null };
type Assignment = { id: string; title: string; description: string | null; due_at: string; subject_id: string | null; max_score: number | null; class_label: string };
type Grade = { id: string; assessment_label: string; score: number; max_score: number; grade_letter: string | null; teacher_comment: string | null; recorded_at: string; subject_id: string };
type Notif = { id: string; kind: string; title: string; body: string | null; created_at: string; read_at: string | null };
type Entitlement = { id: string; bundle_label: string; bundle_mb: number; used_mb: number; minutes_allowed: number; minutes_used: number; expires_at: string; categories_allowed: string[]; categories_blocked: string[]; status: string };
type Reward = { id: string; points: number; source: string; reason: string; occurred_at: string };

// ---------- Utilities ----------
const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const greetings = (h: number) => h < 5 ? "Still up" : h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : h < 21 ? "Good evening" : "Good night";
const partOfDay = (h: number) => h < 5 ? "night" : h < 12 ? "morning" : h < 17 ? "afternoon" : h < 21 ? "evening" : "night";
const partIcon = (h: number) => h < 5 ? Moon : h < 12 ? Sunrise : h < 17 ? Sun : h < 21 ? Sunset : Moon;
// Neutral "Paper & Ink" mood palette — editorial monochrome with subtle warm/cool ink shifts.
const hourMood = (h: number) => {
  if (h < 5) return { label: "Deep night", tint: "#111114", accent: "#8a8a90", ink: "#f5f3ee", icon: Moon };
  if (h < 7) return { label: "First light", tint: "#1b1b20", accent: "#a09a90", ink: "#f5f3ee", icon: Sunrise };
  if (h < 10) return { label: "Fresh morning", tint: "#2a2a2f", accent: "#c9c3b8", ink: "#f5f3ee", icon: Sun };
  if (h < 12) return { label: "Late morning", tint: "#33333a", accent: "#d6d1c6", ink: "#f5f3ee", icon: Sun };
  if (h < 14) return { label: "Midday", tint: "#1f1f24", accent: "#e0dbd0", ink: "#f5f3ee", icon: Sun };
  if (h < 17) return { label: "Slow afternoon", tint: "#26262b", accent: "#cfc9be", ink: "#f5f3ee", icon: Sun };
  if (h < 19) return { label: "Golden hour", tint: "#2b241d", accent: "#c9a878", ink: "#f5f3ee", icon: Sunset };
  if (h < 21) return { label: "Blue hour", tint: "#1a1d24", accent: "#9aa4b4", ink: "#f5f3ee", icon: Sunset };
  return { label: "Late evening", tint: "#0d0d10", accent: "#6f6f76", ink: "#f5f3ee", icon: Moon };
};

function minutesToHM(mins: number) {
  const h = Math.floor(mins / 60); const m = mins % 60;
  return `${h}h ${m}m`;
}

// ---------- Root ----------
export function StudentDashboard() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const firstName = (user?.user_metadata?.full_name ?? user?.email ?? "Student").split(" ")[0];

  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 30_000); return () => clearInterval(t); }, []);

  // Wallet + transactions
  const { data: wallet } = useQuery({
    queryKey: ["student-wallet", user?.id],
    queryFn: async () => (await supabase.from("wallets").select("*").eq("owner_id", user!.id).maybeSingle()).data,
  });
  const { data: txs = [] } = useQuery({
    queryKey: ["student-tx", wallet?.id],
    enabled: !!wallet,
    refetchInterval: 6000,
    queryFn: async () => (await supabase.from("transactions").select("*").eq("wallet_id", wallet!.id).order("created_at", { ascending: false }).limit(30)).data as Tx[] ?? [],
  });

  const { data: bus = [] } = useQuery({
    queryKey: ["student-bus", user?.id],
    refetchInterval: 8000,
    queryFn: async () => (await (supabase.from as any)("bus_check_ins").select("*").eq("student_user_id", user!.id).order("boarded_at", { ascending: false }).limit(10)).data as BusCheckIn[] ?? [],
  });
  const { data: schedule = [] } = useQuery({
    queryKey: ["student-schedule", user?.id],
    queryFn: async () => (await (supabase.from as any)("transport_schedule").select("*").eq("student_user_id", user!.id).order("day_of_week").order("pickup_time")).data ?? [],
  });
  const { data: health = [] } = useQuery({
    queryKey: ["student-health", user?.id],
    queryFn: async () => (await (supabase.from as any)("health_log").select("*").eq("student_user_id", user!.id).order("visit_date", { ascending: false })).data as HealthEntry[] ?? [],
  });
  const { data: reqs = [] } = useQuery({
    queryKey: ["student-requests", user?.id],
    refetchInterval: 6000,
    queryFn: async () => (await (supabase.from as any)("allowance_requests").select("*").eq("student_user_id", user!.id).order("created_at", { ascending: false })).data as AllowanceReq[] ?? [],
  });

  const { data: student } = useQuery({
    queryKey: ["student-me", user?.id],
    queryFn: async () => {
      const { data } = await supabase.from("students").select("*").or(`guardian_id.eq.${user!.id}`).limit(1).maybeSingle();
      return data;
    },
  });
  const classLabel: string | undefined = (student as any)?.class_label;
  const schoolId: string | undefined = (student as any)?.school_id;

  const { data: subjects = [] } = useQuery({
    queryKey: ["student-subjects", schoolId],
    enabled: !!schoolId,
    queryFn: async () => (await supabase.from("subjects").select("*").eq("school_id", schoolId!)).data as Subject[] ?? [],
  });
  const { data: timetable = [] } = useQuery({
    queryKey: ["student-timetable", schoolId, classLabel],
    enabled: !!schoolId && !!classLabel,
    queryFn: async () => (await supabase.from("timetable_slots").select("*").eq("school_id", schoolId!).eq("class_label", classLabel!).order("day_of_week").order("start_time")).data as Slot[] ?? [],
  });
  const { data: assignments = [] } = useQuery({
    queryKey: ["student-assignments", schoolId, classLabel],
    enabled: !!schoolId && !!classLabel,
    queryFn: async () => (await supabase.from("assignments").select("*").eq("school_id", schoolId!).eq("class_label", classLabel!).order("due_at")).data as Assignment[] ?? [],
  });
  const { data: grades = [] } = useQuery({
    queryKey: ["student-grades", (student as any)?.id],
    enabled: !!(student as any)?.id,
    queryFn: async () => (await supabase.from("gradebook_entries").select("*").eq("student_id", (student as any).id).order("recorded_at", { ascending: false })).data as Grade[] ?? [],
  });
  const { data: rewards = [] } = useQuery({
    queryKey: ["student-rewards", (student as any)?.id],
    enabled: !!(student as any)?.id,
    queryFn: async () => (await supabase.from("reward_points").select("*").eq("student_id", (student as any).id).order("occurred_at", { ascending: false })).data as Reward[] ?? [],
  });
  const { data: entitlement } = useQuery({
    queryKey: ["student-internet", (student as any)?.id],
    enabled: !!(student as any)?.id,
    queryFn: async () => (await supabase.from("internet_entitlements").select("*").eq("student_id", (student as any).id).eq("status", "active").maybeSingle()).data as Entitlement | null,
  });
  const { data: notifs = [] } = useQuery({
    queryKey: ["student-notifs", user?.id],
    refetchInterval: 8000,
    queryFn: async () => (await supabase.from("notifications").select("*").eq("user_id", user!.id).order("created_at", { ascending: false }).limit(40)).data as Notif[] ?? [],
  });

  // ── Personal agenda items (user-created hour-by-hour) ─────────────────
  const todayISO = now.toISOString().slice(0, 10);
  const { data: personalAgenda = [] } = useQuery({
    queryKey: ["student-agenda", user?.id, todayISO],
    enabled: !!user?.id,
    refetchInterval: 15_000,
    queryFn: async () => (await (supabase.from as any)("student_agenda_items")
      .select("*")
      .eq("owner_user_id", user!.id)
      .eq("agenda_date", todayISO)
      .order("hour")).data as any[] ?? [],
  });

  const subjectMap = useMemo(() => Object.fromEntries(subjects.map(s => [s.id, s])), [subjects]);
  const todayDow = now.getDay();
  const todaySlots = timetable.filter(s => s.day_of_week === todayDow);
  const nextSlot = useMemo(() => {
    const nowMin = now.getHours() * 60 + now.getMinutes();
    return todaySlots.find(s => {
      const [h, m] = s.start_time.split(":").map(Number);
      return h * 60 + m >= nowMin;
    }) ?? todaySlots[todaySlots.length - 1];
  }, [todaySlots, now]);

  const dueToday = assignments.filter(a => new Date(a.due_at).toDateString() === now.toDateString());
  const dueSoon = assignments.filter(a => {
    const d = new Date(a.due_at).getTime() - now.getTime();
    return d > 0 && d < 3 * 86400000;
  });
  const rewardTotal = rewards.reduce((a, r) => a + r.points, 0);
  const unreadNotifs = notifs.filter(n => !n.read_at);

  const todaySpend = txs
    .filter(t => t.type === "purchase" && new Date(t.created_at).toDateString() === now.toDateString())
    .reduce((a, t) => a + t.amount_ugx, 0);
  const cap = (wallet as any)?.daily_limit_ugx ?? 0;
  const lastBus = bus[0];
  const activeEntitlement = entitlement;
  const internetMinsLeft = activeEntitlement ? Math.max(0, activeEntitlement.minutes_allowed - activeEntitlement.minutes_used) : null;

  const mood = hourMood(now.getHours());
  const PartIcon = partIcon(now.getHours());

  // Streak: count consecutive days with any activity (bus, reward, tx)
  const streak = useMemo(() => {
    const days = new Set<string>();
    [...txs.map(t => t.created_at), ...bus.map(b => b.boarded_at), ...rewards.map(r => r.occurred_at)]
      .forEach(d => days.add(new Date(d).toDateString()));
    let count = 0;
    const cursor = new Date(now);
    while (days.has(cursor.toDateString())) { count++; cursor.setDate(cursor.getDate() - 1); }
    return count || 1;
  }, [txs, bus, rewards, now]);

  return (
    <div className="space-y-8">
      {/* Editorial masthead — Paper & Ink */}
      <div className="relative overflow-hidden rounded-[28px] border border-[#e6e2d9] bg-[#f5f3ee] text-[#111114]">
        {/* Fine grain paper texture via layered gradients */}
        <div className="pointer-events-none absolute inset-0 opacity-[0.5]" style={{
          backgroundImage: "radial-gradient(1200px 400px at 10% -10%, rgba(17,17,20,0.05), transparent 60%), radial-gradient(800px 300px at 100% 120%, rgba(17,17,20,0.06), transparent 60%)"
        }} />
        {/* Rule lines like a folio */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-[#111114]/80" />
        <div className="pointer-events-none absolute inset-x-0 top-3 h-px bg-[#111114]/25" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-[#111114]/80" />
        <div className="pointer-events-none absolute inset-x-0 bottom-3 h-px bg-[#111114]/25" />

        {/* Top folio bar */}
        <div className="relative flex items-center justify-between px-6 pt-5 text-[10px] uppercase tracking-[0.32em] text-[#111114]/70">
          <span>Vol. {String(now.getFullYear()).slice(-2)} · Ed. {String(now.getMonth() + 1).padStart(2, "0")}</span>
          <span className="hidden sm:inline">{mood.label}</span>
          <span>{now.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}</span>
        </div>

        <div className="relative grid gap-6 px-6 pb-7 pt-4 md:grid-cols-[1.5fr_1fr] md:items-end">
          <div>
            <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.28em] text-[#111114]/60">
              <span className="inline-flex h-6 items-center rounded-full border border-[#111114]/25 px-2 tracking-[0.2em]">STUDENT · FOLIO</span>
              <span className="inline-flex items-center gap-1.5"><Flame className="h-3 w-3" /> {streak}-day streak</span>
              <span className="inline-flex items-center gap-1.5"><Trophy className="h-3 w-3" /> {rewardTotal} XP</span>
            </div>
            <h1 className="mt-3 font-display text-[64px] leading-[0.95] tracking-tight md:text-[88px]">
              {greetings(now.getHours())},<br />
              <span className="italic">{firstName}.</span>
            </h1>
            <div className="mt-3 max-w-lg text-sm leading-relaxed text-[#111114]/70">
              A quiet, focused day is waiting. {classLabel && <>Class of <span className="font-medium text-[#111114]">{classLabel}</span>. </>}
              {(student as any)?.boarding && <>Boarding house on tonight&rsquo;s roll. </>}
              Turn the page.
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <div className="hidden items-center gap-3 rounded-full border border-[#111114]/15 bg-white/40 px-4 py-2 text-xs backdrop-blur md:flex">
              <div className="grid h-7 w-7 place-items-center rounded-full bg-[#111114] text-[#f5f3ee]">
                <PartIcon className="h-3.5 w-3.5" />
              </div>
              <div className="leading-tight">
                <div className="text-[9px] uppercase tracking-[0.24em] text-[#111114]/50">Now</div>
                <div className="font-medium">{now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
              </div>
            </div>
            <NotificationsBell />
            {unreadNotifs.length > 0 && (
              <Badge variant="secondary" className="animate-pulse bg-[#111114] text-[#f5f3ee] hover:bg-[#111114]"><Bell className="mr-1 h-3 w-3" />{unreadNotifs.length}</Badge>
            )}
          </div>
        </div>
      </div>

      <Tabs defaultValue="today">
        <TabsList className="flex w-full flex-wrap gap-0 rounded-none border-b border-[#111114]/15 bg-transparent p-0">
          {[
            { v: "today", icon: Sun, label: "Today" },
            { v: "learn", icon: GraduationCap, label: "Learn" },
            { v: "life", icon: Home, label: "Life" },
            { v: "services", icon: Zap, label: "Services" },
            { v: "messages", icon: MessageSquare, label: "Messages" },
          ].map(({ v, icon: I, label }) => (
            <TabsTrigger
              key={v}
              value={v}
              className="relative flex-1 gap-2 rounded-none border-0 bg-transparent px-4 py-3 text-xs uppercase tracking-[0.2em] text-[#111114]/50 data-[state=active]:bg-transparent data-[state=active]:text-[#111114] data-[state=active]:shadow-none data-[state=active]:after:absolute data-[state=active]:after:inset-x-0 data-[state=active]:after:-bottom-px data-[state=active]:after:h-0.5 data-[state=active]:after:bg-[#111114]"
            >
              <I className="h-3.5 w-3.5" />{label}
              {v === "messages" && unreadNotifs.length > 0 && <Badge className="ml-1 h-4 bg-[#111114] px-1.5 text-[10px] text-[#f5f3ee]">{unreadNotifs.length}</Badge>}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* ============ TODAY ============ */}
        <TabsContent value="today" className="mt-5 space-y-5">
          <NextUpHero now={now} nextSlot={nextSlot} subjectMap={subjectMap} lastBus={lastBus} boarding={!!(student as any)?.boarding} mood={mood} />

          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-5">
            <GlanceTile icon={ClipboardCheck} label="Attendance" value="Present" tone="success" hint="Signed in 07:42" />
            <GlanceTile icon={Bus} label="Transport" value={lastBus?.location ?? "Not on bus"} tone={lastBus ? "info" : "muted"} hint={lastBus?.stop ?? "Bus 14 · Naalya"} />
            <GlanceTile icon={Utensils} label="Lunch" value="Rice & beans" tone="success" hint="Included today" />
            <GlanceTile icon={Wifi} label="Internet" value={internetMinsLeft != null ? `${minutesToHM(internetMinsLeft)} left` : "No package"} tone={internetMinsLeft && internetMinsLeft > 30 ? "success" : internetMinsLeft ? "warn" : "muted"} hint="Learning package" />
            <GlanceTile icon={BedDouble} label="Boarding" value={(student as any)?.boarding ? "Roll call 20:00" : "Day scholar"} tone="info" hint="House Green · Bed 12" />
          </div>

          <HorizonTimeline now={now} slots={todaySlots} subjectMap={subjectMap} bus={bus} boarding={!!(student as any)?.boarding} personalAgenda={personalAgenda} userId={user?.id} agendaDate={todayISO} />

          <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
            <div className="space-y-4">
              
              <FocusStudio dueToday={dueToday} dueSoon={dueSoon} subjectMap={subjectMap} />
            </div>
            <div className="space-y-4">
              <QuickActionsRich wallet={wallet} />
              <MoodCheckIn />
              <RecentUpdatesCard notifs={notifs.slice(0, 4)} />
              <WalletTile wallet={wallet} todaySpend={todaySpend} cap={cap} />
            </div>
          </div>
        </TabsContent>

        {/* ============ LEARN ============ */}
        <TabsContent value="learn" className="mt-5 space-y-5">
          <LearnHero dueToday={dueToday} dueSoon={dueSoon} grades={grades} rewardTotal={rewardTotal} streak={streak} />
          <div className="grid gap-4 lg:grid-cols-[1.5fr_1fr]">
            <div className="space-y-4">
              <SubjectConstellation subjects={subjects} grades={grades} assignments={assignments} />
              <StudyLibrary classLabel={classLabel} schoolId={schoolId} subjectMap={subjectMap} />
              <QuestBoard assignments={assignments} subjectMap={subjectMap} />
            </div>
            <div className="space-y-4">
              <StudyTimer />
              <GradesJourney grades={grades} subjectMap={subjectMap} />
              <TimetableWeekCard timetable={timetable} subjectMap={subjectMap} />
            </div>
          </div>
        </TabsContent>

        {/* ============ LIFE ============ */}
        <TabsContent value="life" className="mt-5 space-y-5">
          <LifeHero student={student} bus={bus} rewardTotal={rewardTotal} />
          <div className="grid gap-4 lg:grid-cols-2">
            <TransportCard bus={bus} schedule={schedule} />
            <BoardingCard boarding={!!(student as any)?.boarding} />
          </div>
          <div className="grid gap-4 lg:grid-cols-3">
            <WellbeingCard health={health} />
            <RewardsCard rewards={rewards} total={rewardTotal} />
            <ClubsBoard />
          </div>
          <LiveBusMap routes={lastBus?.route ? [lastBus.route] : ["Bus 14"]} height={340} title="My bus — live" />
        </TabsContent>

        {/* ============ SERVICES ============ */}
        <TabsContent value="services" className="mt-5 space-y-5">
          <div className="grid gap-4 lg:grid-cols-[1.15fr_1fr]">
            <PremiumWalletCard wallet={wallet} txs={txs} todaySpend={todaySpend} cap={cap} firstName={firstName} />
            <PayQRCard wallet={wallet} />
          </div>
          <FundredPayCardShowcase wallet={wallet} />
          <div className="grid gap-4 lg:grid-cols-3">
            <InternetCard entitlement={activeEntitlement} minsLeft={internetMinsLeft} />
            <MealsServiceCardV2 />
            <LibraryLabCardV2 />
          </div>
          <ServicesHub />
          <Card className="p-5">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold"><ShieldCheck className="h-4 w-4 text-primary" />Verify me</div>
            <KycOnboarding role="student" />
          </Card>
        </TabsContent>

        {/* ============ MESSAGES ============ */}
        <TabsContent value="messages" className="mt-5 space-y-5">
          <MessagesStudio notifs={notifs} reqs={reqs} qc={qc} userId={user!.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------- Today: hero ----------
function NextUpHero({ now, nextSlot, subjectMap, lastBus, boarding, mood }: any) {
  const subject = nextSlot?.subject_id ? subjectMap[nextSlot.subject_id] : null;
  const startsIn = nextSlot ? (() => {
    const [h, m] = nextSlot.start_time.split(":").map(Number);
    const target = new Date(now); target.setHours(h, m, 0, 0);
    const diff = Math.round((target.getTime() - now.getTime()) / 60000);
    if (diff <= 0) return "Happening now";
    if (diff < 60) return `in ${diff}m`;
    return `in ${Math.floor(diff / 60)}h ${diff % 60}m`;
  })() : null;

  const subjectColor = subject?.color_hex ?? mood.accent;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
      <Card className="relative overflow-hidden border-0 p-0 shadow-elegant" style={{
        background: `linear-gradient(160deg, ${mood.tint} 0%, #0d0d10 100%)`,
        color: "#f5f3ee",
      }}>
        {/* Grid guides */}
        <div className="pointer-events-none absolute inset-0 opacity-[0.08]" style={{
          backgroundImage: "linear-gradient(#f5f3ee 1px, transparent 1px), linear-gradient(90deg, #f5f3ee 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }} />
        {/* Ambient orb */}
        <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full" style={{
          background: `radial-gradient(closest-side, ${mood.accent}55, transparent 70%)`,
        }} />
        <div className="pointer-events-none absolute left-6 top-6 flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-[#f5f3ee]/50">
          <span className="h-px w-8 bg-[#f5f3ee]/40" /> Editorial
        </div>

        <div className="relative grid gap-8 p-8 pt-14 md:grid-cols-[1.6fr_1fr]">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-[#f5f3ee]/20 px-3 py-1 text-[10px] uppercase tracking-[0.28em] text-[#f5f3ee]/80">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#f5f3ee] opacity-70" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#f5f3ee]" />
              </span>
              Next up · {startsIn}
            </div>
            {nextSlot ? (
              <>
                <div className="mt-6 flex items-baseline gap-4">
                  <div className="font-display text-[104px] leading-[0.85] tracking-tight">{nextSlot.start_time.slice(0, 5)}</div>
                  <div className="font-display text-2xl text-[#f5f3ee]/50">— {nextSlot.end_time.slice(0, 5)}</div>
                </div>
                <div className="mt-6 flex items-center gap-3">
                  <div className="h-10 w-1 rounded-full" style={{ background: mood.accent }} />
                  <div>
                    <div className="font-display text-3xl italic">{subject?.name ?? "Class"}</div>
                    <div className="mt-0.5 text-xs uppercase tracking-[0.22em] text-[#f5f3ee]/60">{nextSlot.room ? `Room ${nextSlot.room}` : "Room TBA"}{nextSlot.teacher_name && <> · {nextSlot.teacher_name}</>}</div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="mt-6 font-display text-6xl italic">Free time.</div>
                <div className="mt-2 max-w-md text-sm text-[#f5f3ee]/70">The manuscript closes early today. Read, walk, or catch up on rest.</div>
              </>
            )}

            <div className="mt-8 flex flex-wrap gap-2">
              <Button size="sm" className="rounded-full border border-[#f5f3ee]/25 bg-transparent text-[#f5f3ee] hover:bg-[#f5f3ee]/10">
                <Compass className="mr-1.5 h-3.5 w-3.5" />Route me
              </Button>
              <Button size="sm" className="rounded-full border border-[#f5f3ee]/25 bg-transparent text-[#f5f3ee] hover:bg-[#f5f3ee]/10">
                <BookOpen className="mr-1.5 h-3.5 w-3.5" />Class notes
              </Button>
              <Button size="sm" className="rounded-full bg-[#f5f3ee] text-[#111114] hover:bg-white">
                <Feather className="mr-1.5 h-3.5 w-3.5" />Ask teacher
              </Button>
            </div>
          </div>

          <div className="relative space-y-3 rounded-2xl border border-[#f5f3ee]/15 bg-[#f5f3ee]/[0.04] p-5 text-sm backdrop-blur-sm">
            <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.28em] text-[#f5f3ee]/50">
              <span>Live pulse</span>
              <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-300" />Live</span>
            </div>
            <PulseRow icon={Clock} label="Right now" value={now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} />
            <PulseRow icon={Bus} label="Transport" value={lastBus?.location ?? "Not on bus"} />
            <PulseRow icon={CheckCircle2} label="Attendance" value="Present" tone="pos" />
            <PulseRow icon={boarding ? BedDouble : Home} label={boarding ? "Roll call" : "Home"} value={boarding ? "20:00" : "16:30"} />
          </div>
        </div>
      </Card>
    </motion.div>
  );
}

function PulseRow({ icon: Icon, label, value, tone }: any) {
  return (
    <div className="flex items-center justify-between border-b border-[#f5f3ee]/10 pb-2 last:border-0 last:pb-0">
      <span className="flex items-center gap-2 text-[#f5f3ee]/70"><Icon className="h-3.5 w-3.5" />{label}</span>
      <span className={`font-medium tabular-nums ${tone === "pos" ? "text-emerald-300" : "text-[#f5f3ee]"}`}>{value}</span>
    </div>
  );
}

function GlanceTile({ icon: Icon, label, value, tone, hint }: any) {
  const dot = tone === "success" ? "bg-emerald-500"
    : tone === "warn" ? "bg-amber-500"
    : tone === "info" ? "bg-sky-500"
    : "bg-neutral-400";
  return (
    <motion.div whileHover={{ y: -2 }}>
      <Card className="group relative overflow-hidden border-[#e6e2d9] bg-[#faf8f3] p-4 shadow-none transition-colors hover:bg-white">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-[#111114]/10" />
        <div className="flex items-start justify-between">
          <div className="grid h-9 w-9 place-items-center rounded-xl border border-[#111114]/10 bg-white text-[#111114]"><Icon className="h-4 w-4" /></div>
          <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.22em] text-[#111114]/50">
            <span className={`h-1.5 w-1.5 animate-pulse rounded-full ${dot}`} /> Live
          </div>
        </div>
        <div className="mt-3 text-[10px] uppercase tracking-[0.22em] text-[#111114]/50">{label}</div>
        <div className="mt-1 truncate font-display text-lg text-[#111114]">{value}</div>
        {hint && <div className="mt-0.5 truncate text-[11px] text-[#111114]/55">{hint}</div>}
      </Card>
    </motion.div>
  );
}

// ---------- Today: Horizon Timeline (hour by hour) ----------
function HorizonTimeline({ now, slots, subjectMap, bus, boarding, personalAgenda = [], userId, agendaDate }: any) {
  type HourEvent = { title: string; icon: any; sub?: string; color?: string; kind: string; id?: string; editable?: boolean };
  const qc = useQueryClient();
  const hours = useMemo(() => Array.from({ length: 24 }, (_, i) => i), []);
  const byHour: Record<number, HourEvent[]> = {};
  const push = (h: number, e: HourEvent) => { (byHour[h] ??= []).push(e); };

  slots.forEach((s: any) => {
    const [h] = s.start_time.split(":").map(Number);
    const subj = s.subject_id ? subjectMap[s.subject_id] : null;
    push(h, { title: subj?.name ?? "Class", icon: BookOpen, sub: s.room ? `Room ${s.room}` : "", color: subj?.color_hex, kind: "class" });
  });
  bus.slice(0, 4).forEach((b: any) => {
    const h = new Date(b.boarded_at).getHours();
    push(h, { title: b.location ?? `${b.route} ${b.direction}`, icon: Bus, sub: b.stop ?? "", color: "#4CA6C9", kind: "bus" });
  });
  push(7, { title: "Breakfast", icon: Coffee, sub: "Dining hall", color: "#C1553A", kind: "meal" });
  push(10, { title: "Break", icon: Leaf, sub: "Canteen", color: "#F6D47A", kind: "break" });
  push(13, { title: "Lunch", icon: Utensils, sub: "Dining hall", color: "#C1553A", kind: "meal" });
  push(16, { title: "Sports & clubs", icon: Trophy, sub: "Field", color: "#7B1416", kind: "sport" });
  if (boarding) {
    push(18, { title: "Study hall", icon: BookMarked, sub: "Prep room", color: "#5B4B8A", kind: "study" });
    push(20, { title: "Roll call", icon: Users, sub: "Dorm", color: "#5B4B8A", kind: "roll" });
    push(22, { title: "Lights out", icon: Moon, sub: "Sleep well", color: "#0B1524", kind: "sleep" });
  }
  // ── User's own agenda items ─────────────────────────────────────────
  const KIND_ICON: Record<string, any> = {
    personal: Pin, study: BookMarked, sport: Trophy, meal: Utensils, meeting: Users,
    reminder: Bell, focus: Target, break: Coffee, creative: Palette, health: HeartPulse,
  };
  personalAgenda.forEach((it: any) => {
    push(it.hour, {
      id: it.id,
      title: it.title,
      sub: it.sub ?? "",
      icon: KIND_ICON[it.kind] ?? Pin,
      color: it.color_hex ?? "#3B4E80",
      kind: it.kind ?? "personal",
      editable: true,
    });
  });

  const scrollRef = useRef<HTMLDivElement>(null);
  const currentHour = now.getHours();
  const minutePct = (now.getMinutes() / 60) * 100;

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const child = el.querySelector<HTMLElement>(`[data-hour="${currentHour}"]`);
    if (child) el.scrollTo({ left: child.offsetLeft - 60, behavior: "smooth" });
  }, [currentHour]);

  // ── Dialog state for add / edit ─────────────────────────────────────
  const [editing, setEditing] = useState<{ id?: string; hour: number; title: string; sub: string; kind: string; color_hex: string } | null>(null);

  const saveMutation = useMutation({
    mutationFn: async (form: NonNullable<typeof editing>) => {
      if (!userId) throw new Error("Not signed in");
      const title = form.title.trim();
      if (!title) throw new Error("Give it a title");
      if (form.id) {
        const { error } = await (supabase.from as any)("student_agenda_items")
          .update({ title, sub: form.sub || null, kind: form.kind, color_hex: form.color_hex, hour: form.hour })
          .eq("id", form.id);
        if (error) throw error;
      } else {
        const { error } = await (supabase.from as any)("student_agenda_items")
          .insert({ owner_user_id: userId, agenda_date: agendaDate, hour: form.hour, title, sub: form.sub || null, kind: form.kind, color_hex: form.color_hex });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success(editing?.id ? "Updated" : "Added to your day");
      qc.invalidateQueries({ queryKey: ["student-agenda", userId, agendaDate] });
      setEditing(null);
    },
    onError: (e: any) => toast.error(e?.message ?? "Could not save"),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase.from as any)("student_agenda_items").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Removed");
      qc.invalidateQueries({ queryKey: ["student-agenda", userId, agendaDate] });
      setEditing(null);
    },
    onError: (e: any) => toast.error(e?.message ?? "Could not remove"),
  });

  const openAdd = (h: number) => setEditing({ hour: h, title: "", sub: "", kind: "personal", color_hex: "#3B4E80" });
  const openEdit = (h: number, ev: HourEvent) => setEditing({
    id: ev.id, hour: h, title: ev.title, sub: ev.sub ?? "", kind: ev.kind || "personal",
    color_hex: ev.color ?? "#3B4E80",
  });

  const KINDS: { id: string; label: string; color: string }[] = [
    { id: "personal", label: "Personal", color: "#3B4E80" },
    { id: "study",    label: "Study",    color: "#5B4B8A" },
    { id: "sport",    label: "Sport",    color: "#7B1416" },
    { id: "meal",     label: "Meal",     color: "#C1553A" },
    { id: "meeting",  label: "Meeting",  color: "#0F3D57" },
    { id: "reminder", label: "Reminder", color: "#A15A00" },
    { id: "focus",    label: "Deep focus", color: "#1a1a2e" },
    { id: "break",    label: "Break",    color: "#F6D47A" },
    { id: "creative", label: "Creative", color: "#8A3B7C" },
    { id: "health",   label: "Health",   color: "#B33A3A" },
  ];

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between border-b p-4">
        <div>
          <div className="flex items-center gap-2 font-display text-xl">
            <Sunrise className="h-5 w-5 text-primary" />Today, hour by hour
          </div>
          <div className="text-xs text-muted-foreground">Tap any hour to add · your day is yours to shape</div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => openAdd(currentHour)}>
            <PlusCircle className="h-3.5 w-3.5" />Add to now
          </Button>
          <Badge variant="secondary" className="gap-1"><Clock className="h-3 w-3" />{now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</Badge>
        </div>
      </div>

      {/* Horizon sky strip */}
      <div className="relative h-6 w-full" style={{
        background: "linear-gradient(90deg, #0b1524 0%, #2b1e3d 20%, #f6b380 33%, #f6d47a 48%, #f4a86b 62%, #a53a2e 78%, #3d3a72 88%, #141a2e 100%)"
      }}>
        <div className="pointer-events-none absolute inset-0" style={{
          backgroundImage: "linear-gradient(to right, rgba(255,255,255,0.12) 1px, transparent 1px)",
          backgroundSize: `${100/24}% 100%`,
        }} />
        <motion.div
          className="absolute top-0 h-full w-[3px] bg-white shadow-[0_0_10px_rgba(255,255,255,0.9)]"
          animate={{ left: `${((currentHour + now.getMinutes()/60) / 24) * 100}%` }}
          transition={{ duration: 0.6 }}
        />
      </div>

      <div ref={scrollRef} className="scrollbar-none flex gap-2 overflow-x-auto p-4">
        {hours.map(h => {
          const events = byHour[h] ?? [];
          const isPast = h < currentHour;
          const isCurrent = h === currentHour;
          const m = hourMood(h);
          return (
            <motion.div
              key={h}
              data-hour={h}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: isPast ? 0.55 : 1, y: 0 }}
              transition={{ delay: h * 0.008 }}
              className={`group relative flex min-w-[128px] shrink-0 flex-col rounded-2xl border transition ${isCurrent ? "border-primary shadow-lg" : "border-border"}`}
              style={{
                background: isCurrent
                  ? `linear-gradient(160deg, ${m.tint}18, ${m.accent}18)`
                  : `linear-gradient(180deg, ${m.tint}08, transparent)`,
              }}
            >
              <div className="flex items-center justify-between border-b px-3 py-1.5">
                <span className={`font-mono text-xs ${isCurrent ? "font-bold text-primary" : "text-muted-foreground"}`}>
                  {String(h).padStart(2, "0")}:00
                </span>
                <div className="flex items-center gap-1">
                  <m.icon className="h-3 w-3 opacity-60" style={{ color: m.accent }} />
                  <button
                    type="button"
                    onClick={() => openAdd(h)}
                    className="opacity-0 transition group-hover:opacity-100"
                    title={`Add at ${String(h).padStart(2, "0")}:00`}
                    aria-label={`Add event at ${String(h).padStart(2, "0")}:00`}
                  >
                    <Plus className="h-3 w-3 text-muted-foreground hover:text-primary" />
                  </button>
                </div>
              </div>
              {isCurrent && (
                <div className="relative h-1 w-full bg-primary/10">
                  <motion.div
                    className="absolute inset-y-0 left-0 bg-primary"
                    animate={{ width: `${minutePct}%` }}
                  />
                </div>
              )}
              <div className="flex min-h-[110px] flex-col gap-1.5 p-2">
                {events.length === 0 && (
                  <button
                    onClick={() => openAdd(h)}
                    className="my-auto flex items-center justify-center gap-1 rounded-lg border border-dashed border-border/60 py-4 text-[10px] text-muted-foreground transition hover:border-primary hover:text-primary"
                  >
                    <Plus className="h-3 w-3" />{h < 6 || h > 22 ? "add" : "open · add"}
                  </button>
                )}
                {events.map((e, i) => (
                  <button
                    key={e.id ?? i}
                    onClick={() => e.editable ? openEdit(h, e) : undefined}
                    disabled={!e.editable}
                    className={`group/item flex w-full items-start gap-1.5 rounded-lg bg-background/70 p-1.5 text-left shadow-sm transition ${e.editable ? "cursor-pointer hover:ring-1 hover:ring-primary/50" : "cursor-default"}`}
                  >
                    <div className="mt-0.5 grid h-5 w-5 place-items-center rounded" style={{ background: `${e.color ?? "var(--primary)"}22`, color: e.color ?? "var(--primary)" }}>
                      <e.icon className="h-3 w-3" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1">
                        <div className="truncate text-[11px] font-semibold leading-tight">{e.title}</div>
                        {e.editable && <Pencil className="h-2.5 w-2.5 shrink-0 text-muted-foreground opacity-0 transition group-hover/item:opacity-100" />}
                      </div>
                      {e.sub && <div className="truncate text-[9px] text-muted-foreground">{e.sub}</div>}
                    </div>
                  </button>
                ))}
              </div>
              {isCurrent && (
                <div className="border-t bg-primary px-2 py-1 text-center text-[10px] font-bold uppercase tracking-wider text-primary-foreground">
                  You are here
                </div>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Add / edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editing?.id ? <><Pencil className="h-4 w-4" />Edit</> : <><PlusCircle className="h-4 w-4" />Add to your day</>}
              <Badge variant="secondary" className="ml-auto font-mono">{String(editing?.hour ?? 0).padStart(2, "0")}:00</Badge>
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label htmlFor="agenda-title" className="text-xs">Title</Label>
                <Input
                  id="agenda-title" autoFocus maxLength={80}
                  placeholder="e.g. Revise algebra, Call Mom, Football practice"
                  value={editing.title}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="agenda-sub" className="text-xs">Note <span className="text-muted-foreground">(optional)</span></Label>
                <Input
                  id="agenda-sub" placeholder="Room / partner / detail"
                  value={editing.sub}
                  onChange={(e) => setEditing({ ...editing, sub: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs">Hour</Label>
                <div className="mt-1 grid grid-cols-8 gap-1">
                  {hours.map(h => (
                    <button
                      key={h} type="button"
                      onClick={() => setEditing({ ...editing, hour: h })}
                      className={`rounded-md border px-1 py-1 font-mono text-[10px] transition ${editing.hour === h ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary/50"}`}
                    >
                      {String(h).padStart(2, "0")}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs">Kind</Label>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {KINDS.map(k => {
                    const active = editing.kind === k.id;
                    return (
                      <button
                        key={k.id} type="button"
                        onClick={() => setEditing({ ...editing, kind: k.id, color_hex: k.color })}
                        className={`flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] transition ${active ? "border-primary shadow-sm" : "border-border hover:border-primary/50"}`}
                        style={active ? { background: `${k.color}18` } : undefined}
                      >
                        <span className="h-2 w-2 rounded-full" style={{ background: k.color }} />
                        {k.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:justify-between">
            {editing?.id ? (
              <Button
                variant="ghost" size="sm"
                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={() => deleteMutation.mutate(editing.id!)}
                disabled={deleteMutation.isPending}
              >
                <Trash2 className="mr-1 h-3.5 w-3.5" />Remove
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditing(null)}>Cancel</Button>
              <Button
                size="sm"
                onClick={() => editing && saveMutation.mutate(editing)}
                disabled={saveMutation.isPending || !editing?.title.trim()}
              >
                {editing?.id ? "Save changes" : "Add to day"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ---------- Today: Focus Studio ----------
function FocusStudio({ dueToday, dueSoon, subjectMap }: any) {
  const items = [...dueToday, ...dueSoon].slice(0, 4);
  return (
    <Card className="p-5">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 font-display text-lg"><Target className="h-4 w-4 text-primary" />Focus studio</div>
        <Badge variant="secondary">{items.length} to conquer</Badge>
      </div>
      {items.length === 0 ? (
        <div className="rounded-2xl bg-emerald-500/10 p-6 text-center text-sm text-emerald-700 dark:text-emerald-400">
          <Sparkles className="mx-auto mb-2 h-6 w-6" />You're all caught up — go build something.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((a: Assignment) => {
            const s = subjectMap[a.subject_id!];
            const hrs = (new Date(a.due_at).getTime() - Date.now()) / 3600000;
            const urgency = hrs < 6 ? "hot" : hrs < 24 ? "warm" : "cool";
            return (
              <div key={a.id} className="group flex items-center gap-3 rounded-xl border p-3 transition hover:shadow-md">
                <div className="grid h-11 w-11 place-items-center rounded-xl font-bold text-white" style={{ background: s?.color_hex ?? "var(--primary)" }}>
                  {s?.code?.slice(0, 2) ?? "??"}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold">{a.title}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {s?.name ?? "—"} · due {new Date(a.due_at).toLocaleDateString([], { weekday: "short", hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
                <Badge className={
                  urgency === "hot" ? "bg-destructive text-destructive-foreground" :
                  urgency === "warm" ? "bg-amber-500 text-white" :
                  "bg-emerald-500 text-white"
                }>
                  {hrs < 1 ? "<1h" : hrs < 24 ? `${Math.round(hrs)}h` : `${Math.round(hrs / 24)}d`}
                </Badge>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function QuickActions() {
  return (
    <Card className="p-4">
      <div className="mb-3 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Quick actions</div>
      <div className="grid grid-cols-2 gap-2">
        <ActionBtn icon={FileText} label="Homework" hue="#7B1416" />
        <RequestAllowanceDialog>
          <button className="group flex flex-col items-center gap-1 rounded-xl border p-3 text-xs transition hover:border-primary hover:bg-primary/5">
            <Wallet className="h-4 w-4 text-primary transition group-hover:scale-110" />Ask parent
          </button>
        </RequestAllowanceDialog>
        <ActionBtn icon={QrCode} label="Pay QR" hue="#C1553A" />
        <ActionBtn icon={MessageSquare} label="Message" hue="#5B4B8A" />
      </div>
    </Card>
  );
}
function ActionBtn({ icon: Icon, label, hue }: any) {
  return (
    <button
      className="group flex flex-col items-center gap-1 rounded-xl border p-3 text-xs transition hover:shadow-md"
      style={{ ["--h" as any]: hue }}
    >
      <Icon className="h-4 w-4 transition group-hover:scale-110" style={{ color: hue }} />
      {label}
    </button>
  );
}

// ---------- Today: Mood Check-in ----------
function MoodCheckIn() {
  const [mood, setMood] = useState<string | null>(null);
  const moods = [
    { key: "great", icon: "😄", label: "Great" },
    { key: "good", icon: "🙂", label: "Good" },
    { key: "meh", icon: "😐", label: "Meh" },
    { key: "low", icon: "😔", label: "Low" },
    { key: "sick", icon: "🤒", label: "Sick" },
  ];
  return (
    <Card className="p-4">
      <div className="mb-2 flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
        <Heart className="h-3 w-3" />How are you feeling?
      </div>
      <div className="flex justify-between gap-1">
        {moods.map(m => (
          <button
            key={m.key}
            onClick={() => { setMood(m.key); toast.success(`Logged: ${m.label}`); }}
            className={`flex-1 rounded-xl border p-2 text-center text-2xl transition ${mood === m.key ? "border-primary bg-primary/10 scale-110" : "hover:border-primary/40"}`}
          >
            {m.icon}
          </button>
        ))}
      </div>
      {mood && (
        <div className="mt-2 rounded-lg bg-primary/5 p-2 text-[11px] text-muted-foreground">
          Nurse & class teacher can see this if you're not okay. <button className="underline">Add a note</button>
        </div>
      )}
    </Card>
  );
}

function RecentUpdatesCard({ notifs }: { notifs: Notif[] }) {
  return (
    <Card className="p-4">
      <div className="mb-3 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Recent updates</div>
      <div className="space-y-2">
        {notifs.length === 0 && <div className="text-xs text-muted-foreground">Nothing new right now.</div>}
        {notifs.map(n => (
          <div key={n.id} className="flex gap-2 text-xs">
            <div className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${n.read_at ? "bg-muted-foreground/40" : "bg-primary animate-pulse"}`} />
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium">{n.title}</div>
              <div className="text-[10px] text-muted-foreground">{shortDateTime(n.created_at)}</div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function WalletTile({ wallet, todaySpend, cap }: any) {
  const pct = cap > 0 ? Math.min(100, (todaySpend / cap) * 100) : 0;
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground"><Wallet className="h-3 w-3" />Wallet</div>
        <span className="text-[10px] text-muted-foreground">{fmtUGX(todaySpend)} / {fmtUGX(cap)}</span>
      </div>
      <div className="mt-1 font-display text-2xl">{fmtUGX(wallet?.balance_ugx ?? 0)}</div>
      <Progress value={pct} className="mt-2 h-1.5" />
    </Card>
  );
}

// ---------- Learn tab ----------
function LearnHero({ dueToday, dueSoon, grades, rewardTotal, streak }: any) {
  const avg = grades.length ? (grades.reduce((a: number, g: Grade) => a + (g.score / g.max_score) * 100, 0) / grades.length) : 0;
  return (
    <Card className="relative overflow-hidden border-0 p-6 text-white" style={{ background: "linear-gradient(120deg, #7B1416 0%, #5B4B8A 60%, #C1553A 100%)" }}>
      <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/10 blur-3xl" />
      <div className="relative grid gap-4 sm:grid-cols-4">
        <HeroStat icon={Rocket} label="Due today" value={dueToday.length} sub="quests" />
        <HeroStat icon={Timer} label="Due this week" value={dueSoon.length} sub="upcoming" />
        <HeroStat icon={TrendingUp} label="Class average" value={`${avg.toFixed(0)}%`} sub={`${grades.length} assessments`} />
        <HeroStat icon={Flame} label="Streak · XP" value={`${streak}d · ${rewardTotal}`} sub="keep going" />
      </div>
    </Card>
  );
}
function HeroStat({ icon: Icon, label, value, sub }: any) {
  return (
    <div className="rounded-2xl bg-white/10 p-4 backdrop-blur">
      <Icon className="h-4 w-4 opacity-80" />
      <div className="mt-2 font-display text-3xl">{value}</div>
      <div className="mt-0.5 text-[10px] uppercase tracking-[0.2em] opacity-70">{label}</div>
      <div className="text-[10px] opacity-60">{sub}</div>
    </div>
  );
}

// Subject Constellation — orbital view
function SubjectConstellation({ subjects, grades, assignments }: any) {
  const [active, setActive] = useState<Subject | null>(null);
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between border-b p-4">
        <div>
          <div className="font-display text-xl">Subject constellation</div>
          <div className="text-xs text-muted-foreground">Each subject orbits your progress — size = mastery, glow = urgency</div>
        </div>
        <Badge variant="secondary">{subjects.length}</Badge>
      </div>
      {subjects.length === 0 ? (
        <div className="p-8 text-center text-sm text-muted-foreground">No subjects yet.</div>
      ) : (
        <div className="grid gap-3 p-4 sm:grid-cols-2 md:grid-cols-3">
          {subjects.map((s: Subject) => {
            const sg = grades.filter((g: Grade) => g.subject_id === s.id);
            const avg = sg.length ? sg.reduce((a: number, g: Grade) => a + (g.score / g.max_score) * 100, 0) / sg.length : 0;
            const pending = assignments.filter((a: Assignment) => a.subject_id === s.id && new Date(a.due_at) > new Date()).length;
            const color = s.color_hex ?? "#7B1416";
            return (
              <motion.button
                key={s.id}
                whileHover={{ y: -3, scale: 1.02 }}
                onClick={() => setActive(s)}
                className="group relative overflow-hidden rounded-2xl border p-4 text-left transition"
                style={{ background: `linear-gradient(160deg, ${color}12, transparent)` }}
              >
                <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full opacity-20" style={{ background: color, filter: "blur(24px)" }} />
                <div className="relative flex items-start justify-between">
                  <div className="grid h-11 w-11 place-items-center rounded-xl font-bold text-white shadow-lg" style={{ background: color }}>
                    {s.code?.slice(0, 2)}
                  </div>
                  <div className="text-right">
                    <div className="font-display text-2xl">{avg ? `${avg.toFixed(0)}` : "—"}<span className="text-xs opacity-60">%</span></div>
                    <div className="text-[10px] uppercase text-muted-foreground">mastery</div>
                  </div>
                </div>
                <div className="mt-3 font-semibold">{s.name}</div>
                <div className="mt-1 text-[11px] text-muted-foreground">{sg.length} grades · {pending} pending</div>
                {/* Orbit ring */}
                <svg className="mt-3 h-2 w-full" viewBox="0 0 100 4" preserveAspectRatio="none">
                  <line x1="0" y1="2" x2="100" y2="2" stroke={`${color}33`} strokeWidth="2" />
                  <line x1="0" y1="2" x2={avg} y2="2" stroke={color} strokeWidth="2" strokeLinecap="round" />
                </svg>
              </motion.button>
            );
          })}
        </div>
      )}
      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{active?.name}</DialogTitle></DialogHeader>
          <div className="text-sm text-muted-foreground">Subject deep-dive coming soon.</div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// Quest Board — assignments as quests
function QuestBoard({ assignments, subjectMap }: any) {
  return (
    <Card className="p-5">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 font-display text-xl"><Rocket className="h-5 w-5 text-primary" />Quest board</div>
        <Badge variant="secondary">{assignments.length}</Badge>
      </div>
      {assignments.length === 0 ? (
        <div className="rounded-xl bg-muted/40 p-6 text-center text-sm text-muted-foreground">No quests posted yet.</div>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2">
          {assignments.slice(0, 6).map((a: Assignment, i: number) => {
            const s = subjectMap[a.subject_id!];
            const xp = (a.max_score ?? 20) * 5;
            const dueSoon = new Date(a.due_at).getTime() - Date.now() < 86400000;
            return (
              <motion.div
                key={a.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className="group relative overflow-hidden rounded-2xl border p-3.5 transition hover:shadow-md"
              >
                <div className="absolute right-0 top-0 h-full w-1" style={{ background: s?.color_hex ?? "var(--primary)" }} />
                <div className="flex items-start justify-between">
                  <Badge variant="secondary" className="gap-1 text-[10px]">
                    <Sparkles className="h-2.5 w-2.5" />+{xp} XP
                  </Badge>
                  {dueSoon && <Badge className="bg-destructive text-destructive-foreground text-[10px]">Hot</Badge>}
                </div>
                <div className="mt-2 font-semibold">{a.title}</div>
                <div className="text-[11px] text-muted-foreground">{s?.name ?? "—"}</div>
                <div className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground">
                  <span>Due {new Date(a.due_at).toLocaleDateString([], { weekday: "short", hour: "2-digit", minute: "2-digit" })}</span>
                  <ChevronRight className="h-3 w-3 transition group-hover:translate-x-1" />
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// Pomodoro-style focus timer
function StudyTimer() {
  const [seconds, setSeconds] = useState(25 * 60);
  const [running, setRunning] = useState(false);
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setSeconds(s => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [running]);
  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");
  const pct = 100 - (seconds / (25 * 60)) * 100;
  return (
    <Card className="p-5 text-center">
      <div className="mb-2 flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
        <Timer className="h-3 w-3" />Focus session
      </div>
      <div className="relative mx-auto grid h-32 w-32 place-items-center">
        <svg viewBox="0 0 100 100" className="absolute inset-0 -rotate-90">
          <circle cx="50" cy="50" r="44" fill="none" stroke="hsl(var(--muted))" strokeWidth="6" />
          <circle cx="50" cy="50" r="44" fill="none" stroke="hsl(var(--primary))" strokeWidth="6" strokeLinecap="round"
            strokeDasharray={2 * Math.PI * 44} strokeDashoffset={2 * Math.PI * 44 * (1 - pct / 100)} />
        </svg>
        <div className="font-display text-3xl tabular-nums">{mm}:{ss}</div>
      </div>
      <div className="mt-3 flex justify-center gap-2">
        <Button size="sm" onClick={() => setRunning(r => !r)}>{running ? "Pause" : "Start"}</Button>
        <Button size="sm" variant="outline" onClick={() => { setSeconds(25 * 60); setRunning(false); }}>Reset</Button>
      </div>
      <div className="mt-2 text-[10px] text-muted-foreground">25-min deep-work · quiet notifications</div>
    </Card>
  );
}

// Grades journey line
function GradesJourney({ grades, subjectMap }: any) {
  return (
    <Card className="p-5">
      <div className="mb-3 font-display text-lg">Grades journey</div>
      {grades.length === 0 ? (
        <div className="text-xs text-muted-foreground">No grades recorded.</div>
      ) : (
        <div className="space-y-2">
          {grades.slice(0, 5).map((g: Grade) => {
            const pct = (g.score / g.max_score) * 100;
            const s = subjectMap[g.subject_id];
            return (
              <div key={g.id} className="rounded-xl border p-2.5">
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="h-2 w-2 shrink-0 rounded-full" style={{ background: s?.color_hex ?? "var(--primary)" }} />
                    <div className="min-w-0">
                      <div className="truncate font-semibold">{g.assessment_label}</div>
                      <div className="truncate text-[10px] text-muted-foreground">{s?.name ?? "—"}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-sm font-semibold">{g.score}/{g.max_score}</div>
                    <div className={`text-[10px] ${pct >= 70 ? "text-emerald-600" : pct >= 50 ? "text-amber-600" : "text-destructive"}`}>{g.grade_letter ?? `${pct.toFixed(0)}%`}</div>
                  </div>
                </div>
                <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-muted">
                  <motion.div
                    initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.8 }}
                    className="h-full" style={{ background: s?.color_hex ?? "var(--primary)" }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function TimetableWeekCard({ timetable, subjectMap }: any) {
  return (
    <Card className="p-5">
      <div className="mb-3 font-display text-lg">This week</div>
      <div className="space-y-2">
        {[1,2,3,4,5].map(d => {
          const slots = timetable.filter((s: Slot) => s.day_of_week === d);
          return (
            <div key={d} className="flex items-start gap-2 text-xs">
              <div className="w-9 shrink-0 rounded bg-primary/10 py-1 text-center font-mono font-semibold text-primary">{dayNames[d]}</div>
              <div className="flex flex-wrap gap-1">
                {slots.length === 0 && <span className="text-muted-foreground">—</span>}
                {slots.map((s: Slot) => (
                  <span key={s.id} className="rounded-full border px-2 py-0.5 text-[10px]" style={{ borderColor: subjectMap[s.subject_id!]?.color_hex ?? "var(--border)", color: subjectMap[s.subject_id!]?.color_hex ?? "inherit" }}>
                    {s.start_time.slice(0,5)} · {subjectMap[s.subject_id!]?.code ?? "?"}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ---------- Life tab ----------
function LifeHero({ student, bus, rewardTotal }: any) {
  return (
    <Card className="relative overflow-hidden border-0 p-6 text-white" style={{ background: "linear-gradient(120deg, #C1553A, #F6D47A)" }}>
      <div className="pointer-events-none absolute -bottom-10 -right-10 h-48 w-48 rounded-full bg-white/15 blur-3xl" />
      <div className="relative flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] opacity-80">Life at school</div>
          <div className="mt-1 font-display text-3xl">Beyond the classroom</div>
          <div className="mt-1 text-sm opacity-85">Transport, dorm, health, clubs, community.</div>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="rounded-xl bg-white/15 px-3 py-2 backdrop-blur">
            <div className="font-display text-xl">{bus.length}</div>
            <div className="text-[10px] uppercase opacity-80">Rides</div>
          </div>
          <div className="rounded-xl bg-white/15 px-3 py-2 backdrop-blur">
            <div className="font-display text-xl">{rewardTotal}</div>
            <div className="text-[10px] uppercase opacity-80">XP</div>
          </div>
          <div className="rounded-xl bg-white/15 px-3 py-2 backdrop-blur">
            <div className="font-display text-xl">{(student as any)?.boarding ? "Board" : "Day"}</div>
            <div className="text-[10px] uppercase opacity-80">Status</div>
          </div>
        </div>
      </div>
    </Card>
  );
}

function TransportCard({ bus, schedule }: any) {
  const last = bus[0];
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between border-b p-4">
        <div>
          <div className="flex items-center gap-2 font-display text-xl"><Bus className="h-5 w-5 text-primary" />Transport</div>
          <div className="text-xs text-muted-foreground">{schedule.length} trips/week</div>
        </div>
        <BusCheckInDialog schedule={schedule} />
      </div>
      <div className="grid gap-3 p-4 sm:grid-cols-2">
        <div className="relative overflow-hidden rounded-xl p-3" style={{ background: "linear-gradient(135deg, hsl(var(--primary)/0.12), transparent)" }}>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Current status</div>
          <div className="mt-1 font-semibold">{last?.location ?? "Not on bus"}</div>
          <div className="text-xs text-muted-foreground">{last ? `${last.stop} · ${shortDateTime(last.boarded_at)}` : "Tap Check in when you board"}</div>
          {last && <div className="mt-2 inline-flex items-center gap-1 text-[10px] text-emerald-600"><Radio className="h-3 w-3 animate-pulse" />Live</div>}
        </div>
        <div className="rounded-xl border p-3">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Next pickup</div>
          <div className="mt-1 font-semibold">{schedule[0]?.route ?? "—"}</div>
          <div className="text-xs text-muted-foreground">{schedule[0] ? `${String(schedule[0].pickup_time).slice(0,5)} · ${schedule[0].stop}` : "No route set"}</div>
        </div>
      </div>
      <div className="divide-y border-t">
        {bus.slice(0, 4).map((b: BusCheckIn) => (
          <div key={b.id} className="flex items-center justify-between p-3 text-xs">
            <div className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-primary" />{b.location ?? b.route}</div>
            <Badge variant="secondary" className="uppercase text-[10px]">{b.direction}</Badge>
          </div>
        ))}
        {bus.length === 0 && <div className="p-4 text-center text-xs text-muted-foreground">No bus rides logged yet.</div>}
      </div>
    </Card>
  );
}

function BoardingCard({ boarding }: any) {
  if (!boarding) {
    return (
      <Card className="p-5">
        <div className="flex items-center gap-2 font-display text-xl"><Home className="h-5 w-5 text-primary" />Day scholar</div>
        <p className="mt-2 text-sm text-muted-foreground">You go home each day. Transport reminders will surface in Today.</p>
        <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
          <div className="rounded-lg border p-2"><div className="text-muted-foreground">Home time</div><div className="font-semibold">16:30</div></div>
          <div className="rounded-lg border p-2"><div className="text-muted-foreground">Gate</div><div className="font-semibold">Main gate</div></div>
        </div>
      </Card>
    );
  }
  const timeline = [
    { time: "18:30", label: "Study hall", icon: BookMarked },
    { time: "19:30", label: "Dinner", icon: Utensils },
    { time: "20:00", label: "Roll call", icon: Users },
    { time: "20:30", label: "Free time", icon: Star },
    { time: "22:00", label: "Lights out", icon: Moon },
  ];
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between border-b p-4">
        <div className="flex items-center gap-2 font-display text-xl"><BedDouble className="h-5 w-5 text-primary" />Boarding · House Green</div>
        <Badge variant="secondary">Bed 12</Badge>
      </div>
      <div className="p-4">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Tonight's flow</div>
        <div className="mt-2 space-y-1.5">
          {timeline.map((t, i) => (
            <div key={i} className="flex items-center gap-3 rounded-lg border p-2 text-xs">
              <div className="grid h-7 w-7 place-items-center rounded-lg bg-primary/10 text-primary"><t.icon className="h-3.5 w-3.5" /></div>
              <div className="flex-1 font-semibold">{t.label}</div>
              <div className="font-mono text-muted-foreground">{t.time}</div>
            </div>
          ))}
        </div>
        <Button size="sm" variant="outline" className="mt-3 w-full"><Feather className="mr-1 h-3 w-3" />Request evening leave</Button>
      </div>
    </Card>
  );
}

function HealthMini({ health }: { health: HealthEntry[] }) {
  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center gap-2 font-display text-lg"><HeartPulse className="h-4 w-4 text-primary" />Wellbeing</div>
      <div className="rounded-xl bg-emerald-500/10 p-3 text-xs text-emerald-700 dark:text-emerald-400">
        <Smile className="mr-1 inline h-3 w-3" />You're doing great. Keep hydrated.
      </div>
      <div className="mt-3 space-y-2 text-xs">
        {health.length === 0 && <div className="text-muted-foreground">No sick bay visits logged.</div>}
        {health.slice(0, 3).map(h => (
          <div key={h.id} className="rounded-lg border p-2">
            <div className="font-semibold">{h.reason}</div>
            <div className="text-[10px] text-muted-foreground">{shortDateTime(h.visit_date)} · {h.status}</div>
          </div>
        ))}
      </div>
      <Button size="sm" variant="outline" className="mt-3 w-full"><HeartPulse className="mr-1 h-3 w-3" />See nurse</Button>
    </Card>
  );
}

function RewardsCard({ rewards, total }: any) {
  const nextTier = Math.ceil((total + 1) / 500) * 500;
  const pct = (total / nextTier) * 100;
  return (
    <Card className="relative overflow-hidden p-4">
      <div className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-amber-400/20 blur-3xl" />
      <div className="relative">
        <div className="mb-2 flex items-center gap-2 font-display text-lg"><Trophy className="h-4 w-4 text-amber-500" />XP & rewards</div>
        <div className="font-display text-4xl">{total}<span className="text-sm font-normal opacity-60"> / {nextTier}</span></div>
        <Progress value={pct} className="mt-2 h-1.5" />
        <div className="mt-1 text-[10px] text-muted-foreground">{nextTier - total} XP to next tier</div>
        <div className="mt-3 space-y-1.5">
          {rewards.slice(0, 3).map((r: Reward) => (
            <div key={r.id} className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-1.5"><Award className="h-3 w-3 text-amber-500" />{r.reason}</div>
              <span className="font-semibold">+{r.points}</span>
            </div>
          ))}
          {rewards.length === 0 && <div className="text-xs text-muted-foreground">Complete quests to earn XP.</div>}
        </div>
      </div>
    </Card>
  );
}

function ClubsCard() {
  const clubs = [
    { icon: Music, name: "Music club", meta: "Thu 4 PM", color: "#5B4B8A" },
    { icon: PartyPopper, name: "Debate", meta: "Wed 4 PM", color: "#C1553A" },
    { icon: Trophy, name: "Football", meta: "Fri 4 PM", color: "#7B1416" },
  ];
  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center gap-2 font-display text-lg"><Star className="h-4 w-4 text-primary" />Squads & clubs</div>
      <div className="space-y-2 text-xs">
        {clubs.map((c, i) => (
          <div key={i} className="flex items-center justify-between rounded-lg border p-2.5 transition hover:shadow-sm">
            <div className="flex items-center gap-2">
              <div className="grid h-7 w-7 place-items-center rounded-lg" style={{ background: `${c.color}22`, color: c.color }}>
                <c.icon className="h-3.5 w-3.5" />
              </div>
              <div>
                <div className="font-semibold">{c.name}</div>
                <div className="text-[10px] text-muted-foreground">{c.meta}</div>
              </div>
            </div>
            <Button size="sm" variant="ghost" className="h-6 text-[10px]">Join</Button>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ---------- Services tab ----------
function PremiumWalletCard({ wallet, txs, todaySpend, cap, firstName }: any) {
  const pct = cap > 0 ? Math.min(100, (todaySpend / cap) * 100) : 0;
  return (
    <Card className="overflow-hidden">
      {/* Holographic credit-card style */}
      <div className="relative overflow-hidden p-6 text-white" style={{
        background: "linear-gradient(135deg, #0f2e21 0%, #7B1416 40%, #C1553A 80%, #F6D47A 100%)",
      }}>
        <div className="pointer-events-none absolute inset-0 opacity-30 mix-blend-overlay" style={{
          backgroundImage: "conic-gradient(from 180deg at 50% 50%, #ff00cc, #3333ff, #00ffcc, #ffcc00, #ff00cc)",
          filter: "blur(60px)",
        }} />
        <div className="relative flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] opacity-70">TriSphere Pay</div>
            <div className="mt-1 text-xs opacity-80">{firstName}</div>
          </div>
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-white/15 backdrop-blur">
            <Wallet className="h-5 w-5" />
          </div>
        </div>
        <div className="relative mt-6">
          <div className="text-[10px] uppercase tracking-widest opacity-60">Balance</div>
          <div className="font-display text-5xl tracking-tight">{fmtUGX(wallet?.balance_ugx ?? 0)}</div>
        </div>
        <div className="relative mt-4 flex items-end justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest opacity-60">Today spent</div>
            <div className="font-mono text-sm">{fmtUGX(todaySpend)} / {fmtUGX(cap)}</div>
          </div>
          <RequestAllowanceDialog>
            <Button size="sm" className="border-0 bg-white/20 text-white backdrop-blur hover:bg-white/30"><Send className="mr-1 h-3 w-3" />Ask guardian</Button>
          </RequestAllowanceDialog>
        </div>
        <div className="relative mt-3 h-1 overflow-hidden rounded-full bg-white/15">
          <div className="h-full bg-white/80 transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="divide-y">
        <div className="flex items-center justify-between border-b p-3">
          <div className="font-display text-base">Recent transactions</div>
          <Badge variant="secondary" className="text-[10px]"><span className="mr-1 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />Live ledger</Badge>
        </div>
        {txs.length === 0 && <div className="p-6 text-center text-xs text-muted-foreground">No activity yet.</div>}
        {txs.slice(0, 6).map((t: Tx) => {
          const credit = t.type === "topup" || t.type === "transfer";
          return (
            <div key={t.id} className="flex items-center justify-between p-3">
              <div className="flex items-center gap-3">
                <div className={`grid h-8 w-8 place-items-center rounded-full ${credit ? "bg-emerald-500/10 text-emerald-600" : "bg-[var(--terracotta,#C1553A)]/10 text-[var(--terracotta,#C1553A)]"}`}>
                  {credit ? <ArrowDownLeft className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}
                </div>
                <div>
                  <div className="text-sm font-semibold">{t.description ?? t.type}</div>
                  <div className="text-[10px] text-muted-foreground">{shortDateTime(t.created_at)}</div>
                </div>
              </div>
              <div className={`text-sm font-semibold ${credit ? "text-emerald-600" : ""}`}>{credit ? "+" : "−"}{fmtUGX(t.amount_ugx)}</div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function PayQRCard({ wallet }: any) {
  const [qrUrl, setQrUrl] = useState("");
  const [flipped, setFlipped] = useState(false);
  useEffect(() => {
    if (!wallet?.id) return;
    QRCode.toDataURL(wallet.id, { margin: 1, width: 320, color: { dark: "#7B1416", light: "#FBFAF5" } }).then(setQrUrl);
  }, [wallet?.id]);
  const copy = () => { if (wallet?.id) { navigator.clipboard.writeText(wallet.id); toast.success("Wallet ID copied"); } };
  return (
    <Card className="flex flex-col items-center p-5">
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Pay by QR</div>
      <div className="font-display text-lg">Show at canteen</div>
      <div className="relative mt-3 h-56 w-56 [perspective:1000px]">
        <motion.div
          className="relative h-full w-full [transform-style:preserve-3d]"
          animate={{ rotateY: flipped ? 180 : 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="absolute inset-0 grid place-items-center rounded-2xl border bg-[var(--ivory,#FBFAF5)] p-3 [backface-visibility:hidden]">
            {qrUrl ? <img src={qrUrl} alt="Wallet QR" className="h-full w-full" /> : <QrCode className="h-16 w-16 text-muted-foreground" />}
          </div>
          <div className="absolute inset-0 grid place-items-center rounded-2xl border bg-primary p-4 text-primary-foreground [backface-visibility:hidden] [transform:rotateY(180deg)]">
            <div className="text-center">
              <div className="text-[10px] uppercase opacity-70">Wallet ID</div>
              <div className="mt-2 break-all font-mono text-xs">{wallet?.id?.slice(0, 24) ?? "—"}…</div>
              <Button size="sm" variant="secondary" className="mt-3" onClick={copy}><Copy className="mr-1 h-3 w-3" />Copy</Button>
            </div>
          </div>
        </motion.div>
      </div>
      <Button size="sm" variant="outline" className="mt-3" onClick={() => setFlipped(f => !f)}>
        {flipped ? "Show QR" : "Show ID"}
      </Button>
      <div className="mt-2 text-[10px] text-muted-foreground">Only school-approved merchants can charge you</div>
    </Card>
  );
}

function InternetCard({ entitlement, minsLeft }: any) {
  if (!entitlement) {
    return (
      <Card className="p-4">
        <div className="mb-2 flex items-center gap-2 font-display text-lg"><WifiOff className="h-4 w-4 text-muted-foreground" />Internet</div>
        <div className="text-xs text-muted-foreground">No active learning package. Ask your school admin to activate one.</div>
      </Card>
    );
  }
  const pct = entitlement.minutes_allowed > 0 ? (entitlement.minutes_used / entitlement.minutes_allowed) * 100 : 0;
  const dataPct = entitlement.bundle_mb > 0 ? (entitlement.used_mb / entitlement.bundle_mb) * 100 : 0;
  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center gap-2 font-display text-lg"><Wifi className="h-4 w-4 text-primary" />{entitlement.bundle_label}</div>
      {/* Twin dials */}
      <div className="grid grid-cols-2 gap-3">
        <Dial value={pct} label="Time" sub={`${minutesToHM(minsLeft)} left`} color="hsl(var(--primary))" />
        <Dial value={dataPct} label="Data" sub={`${entitlement.used_mb}/${entitlement.bundle_mb} MB`} color="#C1553A" />
      </div>
      <div className="mt-3 rounded-lg bg-muted/50 p-2 text-[10px]">
        <div className="font-semibold text-emerald-600">✓ {(entitlement.categories_allowed ?? []).join(", ") || "LMS, library"}</div>
        <div className="font-semibold text-destructive">✗ {(entitlement.categories_blocked ?? []).join(", ") || "social, streaming"}</div>
      </div>
      <Button size="sm" variant="outline" className="mt-3 w-full">Request more access</Button>
    </Card>
  );
}
function Dial({ value, label, sub, color }: any) {
  return (
    <div className="text-center">
      <div className="relative mx-auto h-20 w-20">
        <svg viewBox="0 0 100 100" className="-rotate-90">
          <circle cx="50" cy="50" r="42" fill="none" stroke="hsl(var(--muted))" strokeWidth="8" />
          <circle cx="50" cy="50" r="42" fill="none" stroke={color} strokeWidth="8" strokeLinecap="round"
            strokeDasharray={2 * Math.PI * 42} strokeDashoffset={2 * Math.PI * 42 * (1 - value / 100)} />
        </svg>
        <div className="absolute inset-0 grid place-items-center font-mono text-sm font-semibold">{Math.round(100 - value)}%</div>
      </div>
      <div className="mt-1 text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-[10px]">{sub}</div>
    </div>
  );
}

function MealsServiceCard() {
  return (
    <Card className="overflow-hidden">
      <div className="border-b p-4" style={{ background: "linear-gradient(120deg, hsl(var(--primary)/0.08), transparent)" }}>
        <div className="flex items-center gap-2 font-display text-lg"><Utensils className="h-4 w-4 text-primary" />Meals</div>
      </div>
      <div className="p-4">
        <div className="rounded-xl border p-3">
          <div className="text-[10px] uppercase text-muted-foreground">Today's lunch</div>
          <div className="mt-1 font-semibold">Rice, beans & greens</div>
          <div className="text-xs text-muted-foreground">Included in your meal plan</div>
        </div>
        <div className="mt-3 space-y-1.5 text-xs">
          {[
            { l: "Breakfast", t: "07:00", icon: Coffee },
            { l: "Break", t: "10:30", icon: Leaf },
            { l: "Lunch", t: "13:00", icon: Utensils },
            { l: "Dinner", t: "19:00", icon: Utensils },
          ].map((m, i) => (
            <div key={i} className="flex items-center justify-between rounded-lg border p-2">
              <div className="flex items-center gap-2"><m.icon className="h-3.5 w-3.5 text-primary" />{m.l}</div>
              <span className="font-mono">{m.t}</span>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

function LibraryLabCard() {
  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center gap-2 font-display text-lg"><Library className="h-4 w-4 text-primary" />Library & lab</div>
      <div className="space-y-2 text-xs">
        <div className="flex items-center justify-between rounded-lg border p-2">
          <div className="flex items-center gap-2"><Library className="h-3.5 w-3.5" />Library pass</div>
          <Badge variant="secondary" className="text-[10px] text-emerald-600">Active</Badge>
        </div>
        <div className="flex items-center justify-between rounded-lg border p-2">
          <div className="flex items-center gap-2"><FlaskConical className="h-3.5 w-3.5" />Lab slot</div>
          <Button size="sm" variant="ghost" className="h-6 text-[10px]">Book</Button>
        </div>
        <div className="flex items-center justify-between rounded-lg border p-2">
          <div className="flex items-center gap-2"><PlayCircle className="h-3.5 w-3.5" />Study lounge</div>
          <span className="text-[10px] text-muted-foreground">Open till 21:00</span>
        </div>
        <div className="flex items-center justify-between rounded-lg border p-2">
          <div className="flex items-center gap-2"><Lightbulb className="h-3.5 w-3.5" />Maker space</div>
          <Button size="sm" variant="ghost" className="h-6 text-[10px]">Book</Button>
        </div>
      </div>
    </Card>
  );
}

function RequestsPanel({ reqs }: { reqs: AllowanceReq[] }) {
  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="font-display text-lg">My requests</div>
        <RequestAllowanceDialog>
          <Button size="sm"><Plus className="mr-1 h-3 w-3" />New request</Button>
        </RequestAllowanceDialog>
      </div>
      {reqs.length === 0 && <div className="rounded-lg bg-muted/40 p-4 text-center text-xs text-muted-foreground">No requests yet.</div>}
      <div className="grid gap-2 sm:grid-cols-2">
        {reqs.slice(0, 6).map(r => {
          const Icon = r.status === "approved" ? CheckCircle2 : r.status === "rejected" ? XCircle : Hourglass;
          const color = r.status === "approved" ? "text-emerald-600" : r.status === "rejected" ? "text-destructive" : "text-muted-foreground";
          return (
            <div key={r.id} className="rounded-xl border p-3">
              <div className="flex items-center justify-between">
                <Badge variant="secondary" className="capitalize text-[10px]">{r.status}</Badge>
                <Icon className={`h-3.5 w-3.5 ${color}`} />
              </div>
              <div className="mt-1 font-display text-xl">{fmtUGX(r.amount_ugx)}</div>
              <div className="text-xs text-muted-foreground">{r.reason}</div>
              <div className="mt-1 text-[10px] uppercase text-muted-foreground">{shortDateTime(r.created_at)}</div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ---------- Messages: Studio ----------
function MessagesStudio({ notifs, reqs, qc, userId }: any) {
  const channels = [
    { key: "all", label: "All", icon: Radio, color: "#7B1416" },
    { key: "school", label: "School", icon: GraduationCap, color: "#7B1416", match: (n: Notif) => n.kind?.startsWith("school") },
    { key: "teacher", label: "Teachers", icon: BookOpen, color: "#5B4B8A", match: (n: Notif) => n.kind?.startsWith("teacher") || n.kind?.startsWith("assignment") || n.kind?.startsWith("grade") },
    { key: "parent", label: "Parent", icon: Home, color: "#C1553A", match: (n: Notif) => n.kind?.startsWith("parent") || n.kind?.startsWith("allowance") },
    { key: "alerts", label: "Alerts", icon: Bell, color: "#f59e0b", match: (n: Notif) => n.kind?.startsWith("bus") || n.kind?.startsWith("alert") || n.kind?.startsWith("transport") || n.kind?.startsWith("health") },
    { key: "friends", label: "Squad", icon: Users, color: "#0ea5e9" },
  ];
  const [channel, setChannel] = useState("all");
  const filtered = channel === "all" ? notifs : notifs.filter((n: Notif) => channels.find(c => c.key === channel)?.match?.(n));
  const activeCh = channels.find(c => c.key === channel);
  const pending = reqs.filter((r: AllowanceReq) => r.status === "pending");
  const pinned = notifs.filter((n: Notif) => n.kind === "school.pinned" || n.kind === "alert").slice(0, 2);

  const markAll = useMutation({
    mutationFn: async () => { await supabase.from("notifications").update({ read_at: new Date().toISOString() }).eq("user_id", userId).is("read_at", null); },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["student-notifs"] }),
  });

  return (
    <div className="grid gap-4 lg:grid-cols-[240px_1fr_300px]">
      {/* Channel rail */}
      <Card className="p-3">
        <div className="mb-3 flex items-center justify-between px-1">
          <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Channels</div>
          <button className="text-[10px] text-primary hover:underline" onClick={() => markAll.mutate()}>Read all</button>
        </div>
        <div className="space-y-1">
          {channels.map(c => {
            const count = c.key === "all" ? notifs.filter((n: Notif) => !n.read_at).length
              : c.match ? notifs.filter((n: Notif) => c.match!(n) && !n.read_at).length : 0;
            const active = channel === c.key;
            return (
              <motion.button
                key={c.key}
                whileHover={{ x: 3 }}
                onClick={() => setChannel(c.key)}
                className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm transition ${active ? "text-white" : "hover:bg-muted"}`}
                style={active ? { background: c.color } : {}}
              >
                <span className="flex items-center gap-2">
                  <c.icon className="h-4 w-4" />
                  {c.label}
                </span>
                {count > 0 && (
                  <Badge className={`h-5 min-w-[20px] px-1.5 text-[10px] ${active ? "bg-white text-foreground" : ""}`}>{count}</Badge>
                )}
              </motion.button>
            );
          })}
        </div>
        {pinned.length > 0 && (
          <>
            <div className="mt-4 px-1 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Pinned</div>
            <div className="mt-2 space-y-2">
              {pinned.map((p: Notif) => (
                <div key={p.id} className="rounded-xl border p-2.5 text-[11px]">
                  <div className="flex items-center gap-1 font-semibold"><Pin className="h-3 w-3 text-amber-500" />{p.title}</div>
                  {p.body && <div className="mt-0.5 text-muted-foreground">{p.body.slice(0, 60)}…</div>}
                </div>
              ))}
            </div>
          </>
        )}
      </Card>

      {/* Thread */}
      <Card className="flex flex-col overflow-hidden">
        <div className="flex items-center justify-between border-b p-4" style={{ background: `linear-gradient(120deg, ${activeCh?.color ?? "#7B1416"}0f, transparent)` }}>
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl text-white" style={{ background: activeCh?.color ?? "#7B1416" }}>
              {activeCh && <activeCh.icon className="h-5 w-5" />}
            </div>
            <div>
              <div className="font-display text-lg">{activeCh?.label ?? "All messages"}</div>
              <div className="flex items-center gap-1 text-[10px] text-emerald-600">
                <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
                Live · updated {new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </div>
            </div>
          </div>
          <Button size="sm" variant="outline"><Paperclip className="mr-1 h-3 w-3" />Attach</Button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto p-4" style={{ maxHeight: 560 }}>
          {filtered.length === 0 && (
            <div className="grid h-40 place-items-center text-center text-sm text-muted-foreground">
              <div>
                <CircleDot className="mx-auto mb-2 h-6 w-6 opacity-40" />
                Quiet here. New messages will appear in real time.
              </div>
            </div>
          )}
          <AnimatePresence initial={false}>
            {groupByDay(filtered).map(([day, items]) => (
              <div key={day}>
                <div className="mb-3 flex items-center gap-2">
                  <div className="h-px flex-1 bg-border" />
                  <span className="rounded-full bg-muted px-3 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">{day}</span>
                  <div className="h-px flex-1 bg-border" />
                </div>
                <div className="space-y-3">
                  {items.map((n: Notif) => (
                    <MessageBubble key={n.id} n={n} channels={channels} />
                  ))}
                </div>
              </div>
            ))}
          </AnimatePresence>
        </div>

        <MessageComposer />
      </Card>

      {/* Side rail: approvals + reactions */}
      <div className="space-y-4">
        <Card className="p-4">
          <div className="mb-3 flex items-center gap-2 font-display text-lg"><Flag className="h-4 w-4 text-primary" />Approvals</div>
          {pending.length === 0 && <div className="text-xs text-muted-foreground">Nothing waiting on your guardian.</div>}
          <div className="space-y-2">
            {pending.map((r: AllowanceReq) => (
              <div key={r.id} className="rounded-lg border p-2.5 text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-semibold">{fmtUGX(r.amount_ugx)}</span>
                  <Badge variant="secondary" className="text-[10px]">Pending</Badge>
                </div>
                <div className="mt-0.5 text-[11px] text-muted-foreground">{r.reason}</div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-4">
          <div className="mb-3 flex items-center gap-2 font-display text-lg"><Palette className="h-4 w-4 text-primary" />Vibe check</div>
          <div className="text-[11px] text-muted-foreground">React to school-wide announcements</div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {["🎉","👏","🙌","🔥","🧠","❤️","😴","😅"].map(e => (
              <button key={e} className="rounded-full border px-3 py-1.5 text-lg transition hover:scale-110 hover:border-primary">{e}</button>
            ))}
          </div>
        </Card>

        <Card className="p-4">
          <div className="mb-3 flex items-center gap-2 font-display text-lg"><Users className="h-4 w-4 text-primary" />Squad online</div>
          <div className="flex -space-x-2">
            {["A","B","C","D","+3"].map((l, i) => (
              <div key={i} className={`grid h-8 w-8 place-items-center rounded-full border-2 border-background text-xs font-semibold text-white`} style={{ background: ["#7B1416","#C1553A","#5B4B8A","#0ea5e9","#f59e0b"][i] }}>{l}</div>
            ))}
          </div>
          <div className="mt-3 text-[11px] text-muted-foreground">4 classmates active · say hi</div>
        </Card>
      </div>
    </div>
  );
}

function groupByDay(items: Notif[]): [string, Notif[]][] {
  const map = new Map<string, Notif[]>();
  items.forEach(n => {
    const d = new Date(n.created_at);
    const today = new Date();
    const yest = new Date(); yest.setDate(today.getDate() - 1);
    const key = d.toDateString() === today.toDateString() ? "Today"
      : d.toDateString() === yest.toDateString() ? "Yesterday"
      : d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short" });
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(n);
  });
  return Array.from(map.entries());
}

function MessageBubble({ n, channels }: { n: Notif; channels: any[] }) {
  const bucket = channels.find(c => c.match?.(n)) ?? channels[0];
  const [reacted, setReacted] = useState<string | null>(null);
  const initial = (bucket.label ?? "S")[0];
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className="flex gap-3"
    >
      <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-white text-sm font-bold shadow" style={{ background: bucket.color }}>
        {initial}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <div className="text-sm font-semibold">{bucket.label}</div>
          <div className="text-[10px] text-muted-foreground">{new Date(n.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
          {!n.read_at && <span className="ml-1 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />}
        </div>
        <div className="mt-1 inline-block max-w-full rounded-2xl rounded-tl-sm border bg-card px-3.5 py-2.5 shadow-sm">
          <div className="text-sm font-semibold leading-snug">{n.title}</div>
          {n.body && <div className="mt-0.5 whitespace-pre-line text-xs text-muted-foreground">{n.body}</div>}
        </div>
        <div className="mt-1 flex items-center gap-1">
          {["👍","❤️","🎉","👀"].map(e => (
            <button
              key={e}
              onClick={() => setReacted(reacted === e ? null : e)}
              className={`rounded-full border px-2 py-0.5 text-xs transition hover:scale-110 ${reacted === e ? "border-primary bg-primary/10" : ""}`}
            >
              {e}
            </button>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

function MessageComposer() {
  const [text, setText] = useState("");
  const [recording, setRecording] = useState(false);
  const suggestions = ["Got it, thank you 🙏", "On my way", "Can you resend?", "See you at 4"];
  return (
    <div className="border-t p-3">
      <div className="mb-2 flex flex-wrap gap-1">
        {suggestions.map(s => (
          <button
            key={s}
            onClick={() => setText(s)}
            className="rounded-full border bg-muted/40 px-2.5 py-1 text-[11px] transition hover:border-primary hover:bg-primary/10"
          >
            {s}
          </button>
        ))}
      </div>
      <div className="flex items-end gap-2 rounded-2xl border bg-background p-2">
        <button className="grid h-8 w-8 place-items-center rounded-lg text-muted-foreground hover:bg-muted"><Paperclip className="h-4 w-4" /></button>
        <button
          onClick={() => setRecording(r => !r)}
          className={`grid h-8 w-8 place-items-center rounded-lg transition ${recording ? "bg-destructive text-destructive-foreground animate-pulse" : "text-muted-foreground hover:bg-muted"}`}
        >
          <Mic className="h-4 w-4" />
        </button>
        <Textarea
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder={recording ? "Recording voice note…" : "Write a message"}
          rows={1}
          className="min-h-[36px] resize-none border-0 bg-transparent p-1 text-sm focus-visible:ring-0"
        />
        <button
          disabled={!text.trim()}
          onClick={() => { toast.success("Sent"); setText(""); }}
          className="grid h-9 w-9 place-items-center rounded-lg bg-primary text-primary-foreground disabled:opacity-40"
        >
          <ArrowUp className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// ---------- Dialogs / small buttons ----------
function RequestAllowanceDialog({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("5000");
  const [reason, setReason] = useState("Project supplies for tomorrow");
  const submit = useMutation({
    mutationFn: async () => {
      const amt = Math.max(500, Number(amount));
      const { error } = await (supabase.from as any)("allowance_requests").insert({ student_user_id: user!.id, amount_ugx: amt, reason });
      if (error) throw error;
      await supabase.from("audit_logs").insert({ actor_id: user!.id, action: "allowance.requested", entity_type: "allowance_request", metadata: { amount: amt } });
    },
    onSuccess: () => { toast.success("Sent to your guardian"); setOpen(false); qc.invalidateQueries({ queryKey: ["student-requests"] }); },
    onError: (e: Error) => toast.error(e.message),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Ask your guardian for money</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>Amount (UGX)</Label><Input type="number" value={amount} onChange={e => setAmount(e.target.value)} /></div>
          <div className="space-y-1.5"><Label>What is it for?</Label><Textarea value={reason} onChange={e => setReason(e.target.value)} rows={3} /></div>
        </div>
        <DialogFooter><Button onClick={() => submit.mutate()} disabled={submit.isPending}><Sparkles className="mr-1 h-3 w-3" />Send request</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BusCheckInButton() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const submit = useMutation({
    mutationFn: async () => {
      const hour = new Date().getHours();
      const direction = hour < 12 ? "am" : "pm";
      await (supabase.from as any)("bus_check_ins").insert({
        student_user_id: user!.id, route: "Bus 14", direction,
        stop: direction === "am" ? "Naalya Gate" : "School gate",
        location: direction === "am" ? "On bus 14" : "Headed home",
      });
    },
    onSuccess: () => { toast.success("Boarded ✓"); qc.invalidateQueries({ queryKey: ["student-bus"] }); },
  });
  return <Button size="sm" variant="outline" onClick={() => submit.mutate()} disabled={submit.isPending}><Bus className="mr-1 h-3 w-3" />Check in</Button>;
}
